// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import PropTypes from 'prop-types';
import { PageHeader, Breadcrumbs, Heading, Text } from '@plesk/ui-library';
import Link from 'components/common/Link';
import ObjectSwitcher from './ObjectSwitcher';
import PageContentHeaderNav from './PageContentHeaderNav';
import stripTags from 'helpers/stripTags';
import SafeHtml from 'components/common/SafeHtml';
import JswComponent from 'components/jsw/JswComponent';

const EMPTY_HEADING_CLASS = 'page-content-header--empty-heading';
const EMPTY_PAGE_TITLE_HEADING_CLASS = 'page-content-header__heading--empty';

const hasHtmlContent = value => Boolean(value && stripTags(value).trim());

const PageContentHeader = ({
    breadcrumbs = [],
    tabs = null,
    tabsHasRootPage = false,
    navigationTabs = null,
    hidePathbar = false,
    hideHeading = false,
    wizardTitle = null,
    pageTitle = null,
    pageSubtitle = null,
    titleRightAddon = null,
    objectSwitcherParams = null,
    forceObjectSwitcher = false,
    isClassicMode = false,
    additionalTitleContent = null,
    className = 'page-content-header',
}) => {
    breadcrumbs = Array.isArray(breadcrumbs) ? [...breadcrumbs] : [];
    let currentPage = breadcrumbs.pop();
    let addObjectSwitcher = forceObjectSwitcher;
    let objectName = null;
    let pageTitleSuffix = null;

    if (currentPage && tabs && tabsHasRootPage) {
        currentPage = breadcrumbs.pop();
        if (!pageTitle) {
            pageTitle = currentPage.title;
        }
    }

    if (currentPage && navigationTabs) {
        currentPage = breadcrumbs.pop();
        pageTitle = currentPage.title;
        addObjectSwitcher = pageTitle.indexOf('</b>') !== -1;
    }

    const bOpen = pageTitle ? pageTitle.indexOf('<b>') : -1;
    const bClose = bOpen !== -1 ? pageTitle.indexOf('</b>', bOpen) : -1;
    if (bOpen !== -1 && bClose > bOpen) {
        objectName = pageTitle.slice(bOpen + 3, bClose);
        pageTitleSuffix = pageTitle.slice(bClose + 4);
        pageTitle = pageTitle.slice(0, bOpen);

        const previousPage = breadcrumbs[breadcrumbs.length - 1];
        if (previousPage && stripTags(objectName) === stripTags(previousPage.title)) {
            objectName = (
                <Link to={previousPage.href}>
                    <SafeHtml html={stripTags(previousPage.title)} />
                </Link>
            );
            breadcrumbs.pop();
            addObjectSwitcher = true;
        }
    }

    if ((hidePathbar || breadcrumbs.length) && hideHeading && (!isClassicMode || !navigationTabs)) {
        return null;
    }

    const hasPageTitle = hasHtmlContent(pageTitle);
    const hasWizardTitle = hasHtmlContent(wizardTitle);
    const hasAdditionalTitleContent = Boolean(additionalTitleContent);
    const isEmptyHeading = !hasWizardTitle && !hasPageTitle && !objectName && !hasAdditionalTitleContent;

    const pageHeaderClassName = isEmptyHeading
        ? `${className} ${EMPTY_HEADING_CLASS}`
        : className;

    const renderPageTitle = () => {
        if (objectName) {
            return (
                <Heading level={wizardTitle ? 3 : 1} data-type="page-title">
                    <span aria-live="polite" className="page-content-header__title">
                        {hasPageTitle ? <SafeHtml html={pageTitle} /> : null}
                    </span>
                    {typeof objectName === 'string' ? <SafeHtml html={objectName} /> : objectName}
                    {objectSwitcherParams && addObjectSwitcher ? (
                        <ObjectSwitcher {...objectSwitcherParams} />
                    ) : null}
                    {pageTitleSuffix}
                    {additionalTitleContent && (
                        <JswComponent key={additionalTitleContent} component="span">
                            {additionalTitleContent}
                        </JswComponent>
                    )}
                </Heading>
            );
        }

        if (!hasPageTitle && !hasAdditionalTitleContent && !isEmptyHeading) {
            return null;
        }

        return (
            <Heading
                level={wizardTitle ? 3 : 1}
                data-type="page-title"
                className={isEmptyHeading ? EMPTY_PAGE_TITLE_HEADING_CLASS : undefined}
            >
                <span aria-live="polite" className="page-content-header__title">
                    {/* TODO EXTSSLIT-590: EXTPHPCOMP-33: Some modules embed <a> tags directly in pageTitle. */}
                    {hasPageTitle ? <SafeHtml html={pageTitle} /> : null}
                </span>
                {additionalTitleContent && (
                    <JswComponent key={additionalTitleContent} component="span">
                        {additionalTitleContent}
                    </JswComponent>
                )}
            </Heading>
        );
    };

    return (
        <PageHeader
            className={pageHeaderClassName}
            breadcrumbs={!hidePathbar && breadcrumbs.length ? (
                <Breadcrumbs data-type="page-pathbar">
                    {breadcrumbs.map(({ title, href }) => (
                        <Link key={href} to={href}>
                            <SafeHtml html={stripTags(title)} />
                        </Link>
                    ))}
                </Breadcrumbs>
            ) : null}
            tabs={isClassicMode && navigationTabs ? (
                <PageContentHeaderNav items={navigationTabs} />
            ) : null}
            title={hideHeading ? null : (
                <div>
                    {wizardTitle ? (
                        <Heading>{wizardTitle}</Heading>
                    ) : null}
                    {renderPageTitle()}
                </div>
            )}
            titleRightAddon={titleRightAddon}
        >
            {!hideHeading && pageSubtitle ? (
                <Text intent="muted">{pageSubtitle}</Text>
            ) : null}
        </PageHeader>
    );
};

PageContentHeader.propTypes = {
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
        href: PropTypes.string,
        title: PropTypes.string,
    })),
    tabs: PropTypes.array,
    tabsHasRootPage: PropTypes.bool,
    navigationTabs: PropTypes.array,
    hidePathbar: PropTypes.bool,
    hideHeading: PropTypes.bool,
    wizardTitle: PropTypes.string,
    pageTitle: PropTypes.string,
    pageSubtitle: PropTypes.string,
    titleRightAddon: PropTypes.node,
    objectSwitcherParams: PropTypes.shape(ObjectSwitcher.propTypes),
    forceObjectSwitcher: PropTypes.bool,
    isClassicMode: PropTypes.bool,
    additionalTitleContent: PropTypes.string,
    className: PropTypes.string,
};

export default PageContentHeader;
